import { FileChoiceDialog } from "./FileChoiceDialog";

/**
 * What should happen to the files you just dropped.
 *
 * Proofing used to be a standing toggle: on by default, and the only way to
 * exclude one file from a delivery was to catch it after it had uploaded and
 * already started. That is not a choice, it is a race - and the file it most
 * often applied to (credits, a retail sample, a pronunciation guide) is exactly
 * the one nobody wants a transcript billed for.
 *
 * So the choice is made where it belongs: once, before anything is sent, over
 * the actual list of files.
 */
export function UploadIntentDialog({ files, onCancel, onConfirm }: {
  files: File[];
  onCancel: () => void;
  /** The names to proof on arrival. Everything else is stored and left alone. */
  onConfirm: (proof: Set<string>) => void;
}) {
  return (
    <FileChoiceDialog
      title="Which of these should be proofed?"
      lede={<>
        A ticked file is transcribed and compared against the manuscript as soon as it
        lands. An unticked one is stored and left alone - you can start proofing it, or
        run a spec check, whenever you like.
      </>}
      items={files.map((f) => ({ name: f.name, size: f.size }))}
      tagOn="Proof" tagOff="Upload only"
      confirmLabel={() => `Upload ${files.length} file${files.length === 1 ? "" : "s"}`}
      summary={(n, rest) =>
        n === 0 ? "Nothing will be proofed - every file is stored and left for you to choose a service."
          : rest === 0 ? `All ${n} will be proofed as they arrive.`
            : `${n} will be proofed as ${n === 1 ? "it arrives" : "they arrive"}; ${rest} stored only.`}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}
